import {
  getAuthenticatedHeaders,
  getBaseRESTURL,
  handleDelegationTimeout
} from '@/services/authentication/authentication-service';
import strings from '@/pages/roster/roster.translations';
import { useOptimiserService } from './OptimiserService';
import { OptimiserStatus } from './optimiser.types';
import { useEffect, useState } from 'react';
import { dateToApiFormat } from '@/helpers/dateHelper';

export interface OptimisationEvaluation {
  optimisationEventId?: string;
  strategy?: number;
  solverType?: number;
  locationId?: string | number;
  rosterStartDate?: string;
  rosterEndDate?: string;
  assignedShiftCount?: number;
  unassignedShiftCount?: number;
  coverageRate?: number;
  overallScore?: number;
  summaryJson?: string;
  diagnosticsJson?: string;
  calculatedAt?: string;
  evaluatorVersion?: string;
  solverStatus?: string;
  objectiveValue?: number;
  bestBound?: number;
  gapPercent?: number;
  solveDurationMs?: number;
}

export interface OptimisationEvaluationSummary {
  totalShiftCount?: number;
  assignedShiftCount?: number;
  unassignedShiftCount?: number;
  coveragePercent?: number;
  coverageScore?: number;
  assignmentScore?: number;
  optimalityScore?: number;
  overallScore?: number;
  objectiveValue?: number;
  bestBound?: number;
  gapPercent?: number;
  solveDurationMs?: number;
}

export interface OptimisationEvaluationConstraintResult {
  key: string;
  label?: string;
  description?: string;
  category: string;
  group?: string;
  baselineType?: string;
  issueCount: number;
  evaluatedCount: number;
  satisfiedCount: number;
  satisfactionRate?: number;
  deltaSeconds?: number;
}

export interface OptimisationEvaluationDiagnostics {
  dailyCoverage?: Array<{
    date?: string;
    totalShiftCount?: number;
    assignedShiftCount?: number;
  }>;
  warning?: string;
  scoreBreakdown?: {
    coverageScore?: number;
    assignmentScore?: number;
    optimalityScore?: number;
  };
  constraintResults?: OptimisationEvaluationConstraintResult[];
}

const normaliseConstraintResult = (value: any): OptimisationEvaluationConstraintResult => ({
  key: value?.key ?? value?.Key ?? '',
  label: value?.label ?? value?.Label,
  description: value?.description ?? value?.Description,
  category: value?.category ?? value?.Category ?? '',
  group: value?.group ?? value?.Group,
  baselineType: value?.baselineType ?? value?.BaselineType,
  issueCount: value?.issueCount ?? value?.IssueCount ?? 0,
  evaluatedCount: value?.evaluatedCount ?? value?.EvaluatedCount ?? 0,
  satisfiedCount: value?.satisfiedCount ?? value?.SatisfiedCount ?? 0,
  satisfactionRate: value?.satisfactionRate ?? value?.SatisfactionRate,
  deltaSeconds: value?.deltaSeconds ?? value?.DeltaSeconds
});

const normaliseDiagnostics = (value: any): OptimisationEvaluationDiagnostics => ({
  dailyCoverage: (value?.dailyCoverage ?? value?.DailyCoverage ?? []).map((item: any) => ({
    date: item?.date ?? item?.Date,
    totalShiftCount: item?.totalShiftCount ?? item?.TotalShiftCount,
    assignedShiftCount: item?.assignedShiftCount ?? item?.AssignedShiftCount
  })),
  warning: value?.warning ?? value?.Warning,
  scoreBreakdown: value?.scoreBreakdown ?? value?.ScoreBreakdown
    ? {
        coverageScore:
          value?.scoreBreakdown?.coverageScore ??
          value?.scoreBreakdown?.CoverageScore ??
          value?.ScoreBreakdown?.coverageScore ??
          value?.ScoreBreakdown?.CoverageScore,
        assignmentScore:
          value?.scoreBreakdown?.assignmentScore ??
          value?.scoreBreakdown?.AssignmentScore ??
          value?.ScoreBreakdown?.assignmentScore ??
          value?.ScoreBreakdown?.AssignmentScore,
        optimalityScore:
          value?.scoreBreakdown?.optimalityScore ??
          value?.scoreBreakdown?.OptimalityScore ??
          value?.ScoreBreakdown?.optimalityScore ??
          value?.ScoreBreakdown?.OptimalityScore
      }
    : undefined,
  constraintResults: (value?.constraintResults ?? value?.ConstraintResults ?? []).map(normaliseConstraintResult)
});

export interface OptimisationEvaluationWithSummary extends OptimisationEvaluation {
  summary?: OptimisationEvaluationSummary;
  diagnostics?: OptimisationEvaluationDiagnostics;
}

export const useOptimisationEvaluation = (eventId?: string) => {
  const completeJob = useOptimiserService(s => s.completeJob);
  const [evaluation, setEvaluation] = useState<OptimisationEvaluation | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(!!eventId);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!eventId) {
      setEvaluation(undefined);
      setLoading(false);
      setError(undefined);
      return;
    }

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const loadEvaluation = async () => {
      setLoading(true);
      setError(undefined);
      const baseUrl = await getBaseRESTURL();

      try {
        const response = await fetch(`${baseUrl}/Optimiser/Evaluation/${eventId}`, {
          headers: getAuthenticatedHeaders()
        });
        await handleDelegationTimeout(response);

        if (cancelled) {
          return;
        }

        if (response.status === 404) {
          timeoutId = setTimeout(loadEvaluation, 5000);
          setLoading(true);
          return;
        }

        if (!response.ok) {
          setError(strings.optimiseRosters.evaluation.loadError);
          setLoading(false);
          return;
        }

        const result = (await response.json()) as OptimisationEvaluation;
        setEvaluation(result);
        setLoading(false);
        completeJob(eventId, OptimiserStatus.FINISHED);
      } catch {
        if (!cancelled) {
          setError(strings.optimiseRosters.evaluation.loadError);
          setLoading(false);
        }
      }
    };

    void loadEvaluation();

    return () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [completeJob, eventId]);

  const summary = evaluation?.summaryJson ? (JSON.parse(evaluation.summaryJson) as OptimisationEvaluationSummary) : undefined;
  const diagnostics = evaluation?.diagnosticsJson ? normaliseDiagnostics(JSON.parse(evaluation.diagnosticsJson)) : undefined;

  return { evaluation, summary, diagnostics, loading, error };
};

export const useOptimisationEvaluations = (eventIds: string[]) => {
  const completeJob = useOptimiserService(s => s.completeJob);
  const [evaluations, setEvaluations] = useState<OptimisationEvaluationWithSummary[]>([]);
  const [loading, setLoading] = useState<boolean>(eventIds.length > 0);
  const [error, setError] = useState<string | undefined>(undefined);
  const eventIdsKey = eventIds.join('|');

  useEffect(() => {
    const requestedEventIds = eventIdsKey ? eventIdsKey.split('|').filter(Boolean) : [];

    if (!requestedEventIds.length) {
      setEvaluations([]);
      setLoading(false);
      setError(undefined);
      return;
    }

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const loadEvaluations = async () => {
      setLoading(true);
      setError(undefined);
      const baseUrl = await getBaseRESTURL();
      const queryString = requestedEventIds.map(eventId => `eventIds=${encodeURIComponent(eventId)}`).join('&');

      try {
        const response = await fetch(`${baseUrl}/Optimiser/Evaluations?${queryString}`, {
          headers: getAuthenticatedHeaders()
        });
        await handleDelegationTimeout(response);

        if (cancelled) {
          return;
        }

        if (response.status === 404) {
          timeoutId = setTimeout(loadEvaluations, 5000);
          setLoading(true);
          return;
        }

        if (!response.ok) {
          setError(strings.optimiseRosters.evaluation.loadError);
          setLoading(false);
          return;
        }

        const result = (await response.json()) as OptimisationEvaluation[];
        const mapped = result.map(item => ({
          ...item,
          summary: item.summaryJson ? (JSON.parse(item.summaryJson) as OptimisationEvaluationSummary) : undefined,
          diagnostics: item.diagnosticsJson
            ? normaliseDiagnostics(JSON.parse(item.diagnosticsJson))
            : undefined
        }));

        setEvaluations(mapped);
        if (mapped.length < requestedEventIds.length) {
          timeoutId = setTimeout(loadEvaluations, 5000);
          setLoading(true);
          return;
        }

        setLoading(false);
        requestedEventIds.forEach(eventId => completeJob(eventId, OptimiserStatus.FINISHED));
      } catch {
        if (!cancelled) {
          setError(strings.optimiseRosters.evaluation.loadError);
          setLoading(false);
        }
      }
    };

    void loadEvaluations();

    return () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [completeJob, eventIdsKey]);

  return { evaluations, loading, error };
};

export const useOptimisationEvaluationHistory = (
  locationId?: number,
  rosterStartDate?: Date,
  rosterEndDate?: Date,
  limit: number = 2
) => {
  const [evaluations, setEvaluations] = useState<OptimisationEvaluationWithSummary[]>([]);
  const [loading, setLoading] = useState<boolean>(!!locationId && !!rosterStartDate && !!rosterEndDate);
  const [error, setError] = useState<string | undefined>(undefined);
  const startKey = rosterStartDate ? dateToApiFormat(rosterStartDate) : '';
  const endKey = rosterEndDate ? dateToApiFormat(rosterEndDate) : '';

  useEffect(() => {
    if (!locationId || !startKey || !endKey) {
      setEvaluations([]);
      setLoading(false);
      setError(undefined);
      return;
    }

    let cancelled = false;

    const loadEvaluations = async () => {
      setLoading(true);
      setError(undefined);
      const baseUrl = await getBaseRESTURL();

      try {
        const response = await fetch(
          `${baseUrl}/Optimiser/EvaluationHistory?locationId=${locationId}&rosterStartDate=${encodeURIComponent(startKey)}&rosterEndDate=${encodeURIComponent(endKey)}&limit=${limit}`,
          {
            headers: getAuthenticatedHeaders()
          }
        );
        await handleDelegationTimeout(response);

        if (cancelled) {
          return;
        }

        if (response.status === 404) {
          setEvaluations([]);
          setLoading(false);
          return;
        }

        if (!response.ok) {
          setError(strings.optimiseRosters.evaluation.loadError);
          setLoading(false);
          return;
        }

        const result = (await response.json()) as OptimisationEvaluation[];
        setEvaluations(
          result.map(item => ({
            ...item,
            summary: item.summaryJson ? (JSON.parse(item.summaryJson) as OptimisationEvaluationSummary) : undefined,
            diagnostics: item.diagnosticsJson ? normaliseDiagnostics(JSON.parse(item.diagnosticsJson)) : undefined
          }))
        );
        setLoading(false);
      } catch {
        if (!cancelled) {
          setError(strings.optimiseRosters.evaluation.loadError);
          setLoading(false);
        }
      }
    };

    void loadEvaluations();

    return () => {
      cancelled = true;
    };
  }, [endKey, limit, locationId, startKey]);

  return { evaluations, loading, error };
};
